import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Button,
    Divider,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import DataGridCreateStrategyCampaigns from "src/components/data-grid/activations/data-grid-create-strategy-campaigns";
import { useAddKoBiddingManualCampaignMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

export default function ModalCreateStrategyCampaings({
    open,
    row,
    onClose,
    setLocalPatches,
    refetch
}: {
    open: boolean;
    row: any;
    onClose: () => void;
    setLocalPatches: any;
    refetch: any
}) {
    const [updatedRow, setUpdatedRow] = useState<any[]>([]);

    const [addKoBiddingManualCampaign] = useAddKoBiddingManualCampaignMutation();

    const workspace = useCurrentWorkspace();
    const [selectedMode, setSelectedMode]: any = useState("KO Bidding Manual");

    useEffect(() => {
        if (!row || row.length === 0) return;

        const sanitized = row.map((r: any) => {
            if (!r.device || r.device.length === 0) {
                return { ...r, device: ["desktop"] };
            }
            return r;
        });

        setUpdatedRow(sanitized);
    }, [row]);

    const PORTFOLIO_TYPES = [
        "Target ROAS",
        "Maximize Conversion Value",
        "Target CPA",
        "Maximize Conversions",
    ];

    const isPortfolioStrategy = (row: any) =>
        row.platform?.toLowerCase() === "google" &&
        PORTFOLIO_TYPES.includes(row.bidding_strategy_type);

    const validateMaxCpc = (platform: string, value?: number | null) => {
        if (value == null) return null;

        const num = Number(value);

        if (platform?.toLowerCase() === "Google" && num < 0.01) {
            throw new Error("Max CPC must be at least 0.01 for Google");
        }

        if (platform?.toLowerCase() === "Bing" && num < 0.05) {
            throw new Error("Max CPC must be at least 0.05 for Bing");
        }

        return num;
    };

    const handleSubmit = async () => {
        if (!workspace) return;

        try {
            const payload = updatedRow.map((row) => {
                const isPortfolio = isPortfolioStrategy(row);

                const validatedMaxCpc = validateMaxCpc(row.platform, row.max_cpc);

                const base = {
                    ...row,
                    workspace_id: workspace.id,
                };

                if (isPortfolio) {
                    return {
                        ...base,
                        max_cpc: null,
                        max_cpc_portfolio_strategy: validatedMaxCpc,

                        target_roas: null,
                        target_roas_portfolio_strategy: row.target_roas ?? null,

                        target_cpa: null,
                        target_cpa_portfolio_strategy: row.target_cpa ?? null,
                    };
                }

                return {
                    ...base,
                    max_cpc: validatedMaxCpc,
                    max_cpc_portfolio_strategy: null,
                    target_roas_portfolio_strategy: null,
                    target_cpa_portfolio_strategy: null,
                };
            });

            const result = await addKoBiddingManualCampaign(payload).unwrap();


            enqueueSnackbar(`KO Manual added.`, { variant: "success" });
            refetch();

            updatedRow.forEach((r, index) => {
                const created = result[index];

                setLocalPatches((prev: any) => {
                    const previous = prev[r.id] ?? r;

                    const newDetails = [
                        ...(previous.activations_details?.filter(
                            (d: any) => d.mode !== "KO Bidding Manual"
                        ) ?? []),

                        {
                            ...created,
                            status: true,
                            mode: "KO Bidding Manual",
                            max_cpc: created.max_cpc ?? r.max_cpc,
                        },
                    ];

                    return {
                        ...prev,
                        [r.id]: {
                            ...previous,
                            activations_details: newDetails,
                        },
                    };
                });
            });

            onClose();
        } catch (err) {
            console.error("❌ Error:", err);
            enqueueSnackbar(`Failed to apply strategy`, { variant: "error" });
        }
    };
    const isMaxCpcValid = (row: any) => {
        const value = row.max_cpc;
        if (value == null) return true;

        const num = Number(value);
        const platform = row.platform?.toLowerCase();

        if (platform === "google" && num < 0.01) return false;
        if (platform === "bing" && num < 0.05) return false;

        return true;
    };

    const isFormValid = updatedRow.every(isMaxCpcValid);

    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: "white",
                    borderRadius: 3,
                    py: 3,
                    color: "#FFF",
                    zIndex: 99999,
                },
            }}
        >
            <DialogTitle>
                <Typography color={'rgba(51, 60, 71, 1)'} fontWeight={600} fontSize={17}>
                    Add a new activation
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box mb={"16px"}>
                    <FormControl component="fieldset">
                        <Typography fontSize={"14px"} color={"rgba(99, 115, 129, 1)"}>Select Mode</Typography>

                        <RadioGroup
                            row
                            value={selectedMode ?? ""}
                            onChange={(e) => setSelectedMode(e.target.value)}
                            sx={{ display: "flex", gap: 2 }}
                        >
                            {["KO Bidding Manual"].map((mode) => (
                                <FormControlLabel
                                    key={mode}
                                    value={mode}
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: "rgba(145, 158, 171, 1)",
                                                "&.Mui-checked": {
                                                    color: "rgba(51, 60, 71, 1)",
                                                },
                                                "&:hover": {
                                                    backgroundColor: "rgba(200, 200, 200, 0.1)",
                                                },
                                            }}
                                        />
                                    }
                                    label={mode}
                                    sx={{
                                        color: selectedMode === mode ? "rgba(51, 60, 71, 1)" : "rgba(99, 115, 129, 1)",
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider sx={{ borderColor: "rgba(145, 158, 171, 0.20))" }} />

                <Box mt={"16px"}>
                    {open && row && (
                        <DataGridCreateStrategyCampaigns
                            rows={updatedRow}
                            onChange={(newRow) => setUpdatedRow(newRow)}
                            mode={selectedMode}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button sx={{
                    backgroundColor: "rgba(145, 158, 171, 0.12)", color: "rgba(145, 158, 171, 1)",
                    "&:hover": {
                        backgroundColor: "rgba(145, 158, 171, 0.24)",
                    },
                }} onClick={onClose} variant="contained" color="inherit">
                    Cancel
                </Button>
                <Button
                    disabled={!isFormValid}
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        cursor: !isFormValid ? "not-allowed" : "pointer",

                        backgroundColor: "rgba(24, 24, 24, 1)",
                        "&:hover": {
                            backgroundColor: "rgba(24, 24, 24, .9)",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "rgba(24, 24, 24, 0.3)",
                            color: "rgba(255,255,255,0.4)",
                            cursor: "not-allowed",
                        },
                    }}
                >
                    Activate
                </Button>
            </DialogActions>
        </Dialog>
    );
}